import React, { useMemo, useState } from "react";
import {
  useHistoricalResultsQuery,
  useTrackPerformanceQuery,
} from "@/api/vercel";
import { useRetryableQuery } from "@/hooks/useRetryableQuery";
import { cn } from "@/lib/utils";
import {
  calculateSystemMetrics,
  getNoDataContext,
  validateMetrics,
} from "@/utils/dashboard-metrics";
import { logger } from "@/utils/logger";
import { DISPLAY_TIME_ZONE, formatTimeOfDay } from "@/utils/time-format";
import { ChevronDown, ChevronUp } from "lucide-react";
import DashboardIcon from "@/components/icons/dashboard-icon";
import GaugeIcon from "@/components/icons/gauge-icon";
import TrophyIcon from "@/components/icons/trophy-icon";
import { DashboardLoadingSkeleton } from "@/components/ui/DashboardLoadingSkeleton";
import { MetricCard } from "@/components/ui/MetricCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

/**
 * AnalysisDashboard component that displays system-wide performance metrics
 * at the top of the analysis page
 */
export function AnalysisDashboard({
  trackPerformanceData,
  historicalData,
  isLoading,
  error,
  className,
  ...props
}) {
  // State for mobile toggle functionality - single state for all cards
  const [showAllDetails, setShowAllDetails] = useState(false);

  // Filter out non-DOM props to prevent them from being spread to DOM elements
  const {
    trackPerformanceData: _trackPerformanceData,
    historicalData: _historicalData,
    isLoading: _isLoading,
    error: _error,
    ...domProps
  } = props;
  // Use provided data props or fallback to fetching data if not provided
  const shouldFetchData = !trackPerformanceData && !isLoading && !error;

  const {
    data: fallbackTrackData,
    isLoading: isFallbackTrackLoading,
    error: fallbackTrackError,
  } = useTrackPerformanceQuery({
    // Must exceed the total number of tracks with performance data (~210) so
    // system-wide metrics aren't computed from a truncated set.
    limit: 1000,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: shouldFetchData,
    retry: (failureCount, error) => {
      // Custom retry logic - retry up to 3 times for network errors
      if (
        failureCount < 3 &&
        (error?.code === "NETWORK_ERROR" || error?.status >= 500)
      ) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Fetch historical results data (optional for additional context)
  const {
    data: fallbackHistoricalData,
    isLoading: isFallbackHistoricalLoading,
    error: fallbackHistoricalError,
  } = useHistoricalResultsQuery({
    fetchAll: false,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: shouldFetchData,
    retry: (failureCount, error) => {
      // Custom retry logic for historical data (less aggressive)
      if (
        failureCount < 2 &&
        (error?.code === "NETWORK_ERROR" || error?.status >= 500)
      ) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 30000),
  });

  // Setup retry functionality for manual retries
  const trackDataRetry = useRetryableQuery(["trackPerformance"], {
    maxRetries: 3,
    baseDelay: 1000,
    onRetrySuccess: (attempt) => {
      logger.log(`Track data retry successful after ${attempt} attempts`);
    },
    onRetryFailure: (error, attempt) => {
      logger.error(`Track data retry ${attempt} failed:`, error);
    },
    onMaxRetriesReached: () => {
      logger.error("Maximum retry attempts reached for track data");
    },
  });

  const historicalDataRetry = useRetryableQuery(["historicalResults"], {
    maxRetries: 2,
    baseDelay: 2000,
    onRetrySuccess: (attempt) => {
      logger.log(`Historical data retry successful after ${attempt} attempts`);
    },
    onRetryFailure: (error, attempt) => {
      logger.error(`Historical data retry ${attempt} failed:`, error);
    },
  });

  // Use provided data or fallback data
  const finalTrackData = trackPerformanceData || fallbackTrackData;
  const finalHistoricalData = historicalData || fallbackHistoricalData;
  const finalIsLoading =
    isLoading ?? (isFallbackTrackLoading || isFallbackHistoricalLoading);
  const finalError = error || fallbackTrackError || fallbackHistoricalError;

  // Calculate system metrics from track data
  const systemMetrics = useMemo(() => {
    if (!finalTrackData) {
      return null;
    }

    const metrics = calculateSystemMetrics(finalTrackData, finalHistoricalData);

    // Validate the calculated metrics
    if (!validateMetrics(metrics)) {
      logger.warn("Invalid metrics calculated:", metrics);
      return null;
    }

    return metrics;
  }, [finalTrackData, finalHistoricalData]);

  // Calculate individual tip position metrics (T1-T8)
  const tipPositionMetrics = useMemo(() => {
    if (!finalTrackData?.data) {
      return null;
    }

    const positions = {
      T1: { bets: 0, wins: 0, returns: 0, profit: 0 },
      T2: { bets: 0, wins: 0, returns: 0, profit: 0 },
      T3: { bets: 0, wins: 0, returns: 0, profit: 0 },
      T4: { bets: 0, wins: 0, returns: 0, profit: 0 },
      T5: { bets: 0, wins: 0, returns: 0, profit: 0 },
      T6: { bets: 0, wins: 0, returns: 0, profit: 0 },
      T7: { bets: 0, wins: 0, returns: 0, profit: 0 },
      T8: { bets: 0, wins: 0, returns: 0, profit: 0 },
    };

    finalTrackData.data.forEach((track) => {
      if (!track.models?.HayiAuto) return;

      const trackData = track.models.HayiAuto;

      // Process early positions (T1-T4)
      Object.entries(trackData.early.positions).forEach(([posKey, posData]) => {
        const tipNumber = posKey.replace("p", ""); // p1 -> 1
        const tipKey = `T${tipNumber}`;

        if (positions[tipKey]) {
          positions[tipKey].bets += posData.bets || 0;
          positions[tipKey].wins += posData.wins || 0;
          positions[tipKey].returns += posData.returns || 0;
          positions[tipKey].profit += posData.profits || posData.profit || 0;
        }
      });

      // Process race time positions (T5-T8)
      Object.entries(trackData.raceTime.positions).forEach(
        ([posKey, posData]) => {
          const tipNumber = parseInt(posKey.replace("p", "")) + 4; // p1 -> 5, p2 -> 6, etc.
          const tipKey = `T${tipNumber}`;

          if (positions[tipKey]) {
            positions[tipKey].bets += posData.bets || 0;
            positions[tipKey].wins += posData.wins || 0;
            positions[tipKey].returns += posData.returns || 0;
            positions[tipKey].profit += posData.profits || posData.profit || 0;
          }
        },
      );
    });

    // Calculate ROI and win rate for each position
    Object.keys(positions).forEach((tipKey) => {
      const pos = positions[tipKey];
      pos.roi = pos.bets > 0 ? ((pos.returns - pos.bets) / pos.bets) * 100 : 0;
      pos.winRate = pos.bets > 0 ? (pos.wins / pos.bets) * 100 : 0;
    });

    return positions;
  }, [finalTrackData]);

  // Define metric configurations with custom icons
  const metricConfigs = useMemo(
    () => [
      {
        key: "totalROI",
        title: "Overall ROI",
        icon: <GaugeIcon className="w-4 h-4 text-blue-600" />,
        formatter: (value) => `${Number(value).toFixed(1)}%`,
        trendCalculator: (value) => {
          const numValue = Number(value);
          if (numValue > 0) return "positive";
          if (numValue < 0) return "negative";
          return "neutral";
        },
        description: "Return on investment across all tips",
      },
      {
        key: "totalWinRate",
        title: "Win Rate",
        icon: <DashboardIcon className="w-4 h-4 text-purple-600" />,
        formatter: (value) => `${Number(value).toFixed(1)}%`,
        trendCalculator: (value) => {
          const numValue = Number(value);
          if (numValue >= 20) return "positive";
          if (numValue >= 10) return "neutral";
          return "negative";
        },
        description: "Percentage of tips that resulted in wins",
      },
      {
        key: "totalWins",
        title: "Total Wins",
        icon: <TrophyIcon className="w-4 h-4" fill="#fbbf24" />,
        formatter: (value) => Number(value).toLocaleString(),
        trendCalculator: (value) =>
          Number(value) > 0 ? "positive" : "neutral",
        description: "Total number of winning tips",
      },
      {
        key: "netReturns",
        title: "Net Returns",
        icon: <span className="text-green-600 font-bold text-sm">$</span>,
        formatter: (value) =>
          new Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: "AUD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(Number(value)),
        trendCalculator: (value) => {
          const numValue = Number(value);
          if (numValue > 0) return "positive";
          if (numValue < 0) return "negative";
          return "neutral";
        },
        description: "Returns minus $1 stakes, all tips",
      },
      {
        key: "totalTips",
        title: "Total Tips",
        icon: <span className="text-gray-600 font-bold text-sm">#</span>,
        formatter: (value) => Number(value).toLocaleString(),
        trendCalculator: (value) =>
          Number(value) > 0 ? "positive" : "neutral",
        description: "Total number of tips provided",
      },
    ],
    [],
  );

  // Determine loading state
  const isLoadingState = finalIsLoading;

  // Determine error state
  const hasError = finalError;

  // Loading state with enhanced skeleton
  if (isLoadingState) {
    return <DashboardLoadingSkeleton className={className} {...domProps} />;
  }

  // Enhanced error state with retry functionality
  if (hasError) {
    const now = new Date();
    const isWeekend = now.getDay() === 0 || now.getDay() === 6;
    const isEarlyMorning = now.getHours() < 8;

    let errorContext =
      "Please try refreshing the page or check back in a few minutes.";
    let errorSuggestion = "The issue may be temporary.";

    if (isWeekend) {
      errorContext = "Racing data may be limited on weekends.";
      errorSuggestion =
        "Check back during weekdays for more comprehensive data.";
    } else if (isEarlyMorning) {
      errorContext = "Racing data is still being prepared for today.";
      errorSuggestion = "Try again after 8:00 AM when racing begins.";
    }

    // Determine which retry functionality to use
    const canRetryTrackData = trackDataRetry.canRetry && fallbackTrackError;
    const canRetryHistoricalData =
      historicalDataRetry.canRetry && fallbackHistoricalError;
    const isRetrying =
      trackDataRetry.isRetrying || historicalDataRetry.isRetrying;

    const handleRetry = async () => {
      const promises = [];

      if (canRetryTrackData) {
        promises.push(trackDataRetry.retry());
      }

      if (canRetryHistoricalData) {
        promises.push(historicalDataRetry.retry());
      }

      if (promises.length === 0) {
        // Fallback to page refresh if no specific retry available
        window.location.reload();
        return;
      }

      try {
        await Promise.all(promises);
      } catch (error) {
        logger.error("Retry failed:", error);
      }
    };

    return (
      <Card
        className={cn(
          "p-4 xs:p-6 sm:p-8 border border-red-200 shadow-lg bg-red-50",
          className,
        )}
        {...domProps}
      >
        <div className="text-center">
          <h2 className="text-lg xs:text-xl font-bold text-red-800 mb-2">
            Analysis Dashboard
          </h2>
          <div className="mb-4">
            <p className="text-red-700 font-medium mb-2 text-sm xs:text-base">
              Unable to load performance data
            </p>
            <p className="text-red-600 text-xs xs:text-sm mb-1 leading-relaxed">
              {errorContext}
            </p>
            <p className="text-red-500 text-xs xs:text-sm mb-3 leading-relaxed">
              {errorSuggestion}
            </p>
          </div>

          {/* Retry button */}
          <div className="mb-4">
            <Button
              onClick={handleRetry}
              disabled={
                isRetrying || (!canRetryTrackData && !canRetryHistoricalData)
              }
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100 hover:border-red-400 disabled:opacity-50"
            >
              {isRetrying ? (
                <>
                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Retrying...
                </>
              ) : (
                <>
                  Try Again
                  {(trackDataRetry.retryCount > 0 ||
                    historicalDataRetry.retryCount > 0) && (
                    <span className="ml-1 text-xs">
                      (
                      {Math.max(
                        trackDataRetry.retryCount,
                        historicalDataRetry.retryCount,
                      )}
                      /
                      {Math.max(
                        trackDataRetry.maxRetries,
                        historicalDataRetry.maxRetries,
                      )}
                      )
                    </span>
                  )}
                </>
              )}
            </Button>
          </div>

          {/* Technical details */}
          <details className="text-left bg-red-100 rounded-lg p-3 mb-4">
            <summary className="text-xs xs:text-sm font-medium text-red-800 cursor-pointer hover:text-red-900 transition-colors">
              Technical Details
            </summary>
            <div className="mt-2 text-xxs xs:text-xs text-red-600 space-y-1">
              {fallbackTrackError && (
                <div className="break-words">
                  <span className="font-medium">Track Data Error:</span>{" "}
                  {fallbackTrackError.message}
                </div>
              )}
              {fallbackHistoricalError && (
                <div className="break-words">
                  <span className="font-medium">Historical Data Error:</span>{" "}
                  {fallbackHistoricalError.message}
                </div>
              )}
              {error && (
                <div className="break-words">
                  <span className="font-medium">General Error:</span>{" "}
                  {error.message}
                </div>
              )}
              <div className="break-words">
                <span className="font-medium">Retry Attempts:</span>
                Track: {trackDataRetry.retryCount}/{trackDataRetry.maxRetries},
                Historical: {historicalDataRetry.retryCount}/
                {historicalDataRetry.maxRetries}
              </div>
            </div>
          </details>

          <div className="text-xxs xs:text-xs text-red-400">
            Error occurred at:{" "}
            {formatTimeOfDay(now)}
          </div>
        </div>
      </Card>
    );
  }

  // No data state with contextual messaging
  if (!systemMetrics) {
    const noDataContext = getNoDataContext();
    const now = new Date();

    return (
      <Card
        className={cn(
          "p-4 xs:p-6 sm:p-8 border border-gray-200 shadow-lg bg-gray-50",
          className,
        )}
        {...domProps}
      >
        <div className="text-center">
          <h2 className="text-lg xs:text-xl font-bold text-gray-800 mb-2">
            Analysis Dashboard
          </h2>
          <div className="mb-4">
            <p className="text-gray-700 font-medium mb-1 text-sm xs:text-base">
              {noDataContext.message}
            </p>
            <p className="text-gray-500 text-xs xs:text-sm mb-2 leading-relaxed">
              {noDataContext.subtitle}
            </p>
            <p className="text-gray-400 text-xxs xs:text-xs leading-relaxed">
              {noDataContext.suggestion}
            </p>
          </div>
          <div className="text-xxs xs:text-xs text-gray-400">
            Last checked:{" "}
            {formatTimeOfDay(now)}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        "p-4 xs:p-6 sm:p-8 border border-[#F30099]/20 shadow-lg bg-white",
        className,
      )}
      {...domProps}
    >
      {/* Tip Position Performance Section */}
      {tipPositionMetrics && (
        <div className="mb-4 sm:mb-6">
          <div className="mb-4">
            <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-1 md:mb-2 truncate">
              All Tips Performance
            </h2>
            <div className="flex flex-col md:flex-row gap-1 justify-between md:items-center">
              <p className="text-gray-600 text-sm md:text-base">
                Based on $1 unit betting on every tip that Lexi provided
                (including exotics) since inception.
              </p>
              <Button
                onClick={() => {
                  const element = document.getElementById(
                    "all-tracks-performance",
                  );
                  if (element) {
                    // Calculate offset for fixed navbar (typically 64px)
                    const navbarHeight = 80; // Adjust this value based on your navbar height
                    const elementPosition =
                      element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - navbarHeight;

                    window.scrollTo({
                      top: offsetPosition,
                      behavior: "smooth",
                    });
                  }
                }}
                variant="outline"
                size="sm"
                className="mt-0 mt-3 md:mt-0 px-4 md:px-3 py-3 md:py-2 border-[#F30099] text-[#F30099] hover:bg-[#F30099] hover:text-white transition-all duration-200 w-fit rounded-lg shadow-sm hover:shadow-md border"
              >
                <span className="flex items-center gap-2">
                  Jump to track-specific performance
                  <svg
                    className="w-4 h-4 transition-transform group-hover:translate-y-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                </span>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 xs:grid-cols-2 md:grid-cols-4 xl:grid-cols-8 auto-cols-auto gap-3 xs:gap-4">
            {Object.entries(tipPositionMetrics).map(([tipKey, metrics]) => {
              const tipNumber = parseInt(tipKey.replace("T", ""));
              const isEarlyMorning = tipNumber <= 4;
              const timingLabel = isEarlyMorning
                ? "Early Morning"
                : "Race Time";
              const timingColor = isEarlyMorning
                ? "bg-blue-50 text-blue-700"
                : "bg-purple-50 text-purple-700";

              // Find best performers
              const allMetrics = Object.values(tipPositionMetrics);
              const bestROI = Math.max(...allMetrics.map((m) => m.roi));
              const bestWinRate = Math.max(...allMetrics.map((m) => m.winRate));
              const mostWins = Math.max(...allMetrics.map((m) => m.wins));
              const bestProfit = Math.max(...allMetrics.map((m) => m.profit));

              // Determine if this tip is a winner in any category
              const isBestROI = metrics.roi === bestROI && metrics.roi > 0;
              const isBestWinRate =
                metrics.winRate === bestWinRate && metrics.winRate > 0;
              const isMostWins = metrics.wins === mostWins && metrics.wins > 0;
              const isBestProfit =
                metrics.profit === bestProfit && metrics.profit > 0;

              // Determine highlight tag
              let highlightTag = null;
              let highlightColor = "";

              if (isBestROI) {
                highlightTag = "Best ROI";
                highlightColor = "bg-green-500 text-white";
              } else if (isBestWinRate) {
                highlightTag = "Best Win Rate";
                highlightColor = "bg-blue-500 text-white";
              } else if (isMostWins) {
                highlightTag = "Most Wins";
                highlightColor = "bg-yellow-500 text-white";
              } else if (isBestProfit) {
                highlightTag = "Top net result";
                highlightColor = "bg-purple-500 text-white";
              }

              return (
                <div
                  key={tipKey}
                  onClick={() => setShowAllDetails(!showAllDetails)}
                  className={`flex flex-col justify-start bg-white border rounded-lg overflow-hidden hover:shadow-md transition-shadow md:cursor-default cursor-pointer ${
                    highlightTag
                      ? "border-2 border-yellow-400 shadow-lg"
                      : "border-gray-200"
                  }`}
                >
                  {/* Header with timing label */}
                  <div
                    className={`px-2 py-2 ${timingColor} border-b border-gray-100`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <h4 className="text-sm font-bold">{tipKey}</h4>
                      <span className="text-xs font-medium text-left">
                        {timingLabel}
                      </span>
                    </div>
                  </div>

                  {/* Main metrics */}
                  <div className="pb-0 md:pb-3 p-3">
                    {/* ROI - Primary metric */}
                    <div className="text-center mb-3">
                      <div
                        className={`text-xl font-bold ${
                          metrics.roi > 0
                            ? "text-green-600"
                            : metrics.roi < 0
                              ? "text-red-600"
                              : "text-gray-600"
                        }`}
                      >
                        {metrics.roi.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">ROI</div>
                    </div>

                    {/* Win Rate - Secondary metric */}
                    <div className="text-center mb-3">
                      <div
                        className={`text-base font-semibold ${
                          metrics.winRate >= 20
                            ? "text-green-600"
                            : metrics.winRate >= 10
                              ? "text-yellow-600"
                              : "text-red-600"
                        }`}
                      >
                        {metrics.winRate.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">Win Rate</div>
                    </div>

                    {/* Compact stats - Hidden on mobile unless toggled */}
                    <div
                      className={`space-y-1 text-xs transition-all duration-300 overflow-hidden ${
                        showAllDetails ? "md:block" : "hidden md:block"
                      }`}
                    >
                      <div className="flex justify-between">
                        <span className="text-gray-500">Tips:</span>
                        <span className="font-medium">
                          {metrics.bets.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Wins:</span>
                        <span className="font-medium">
                          {metrics.wins.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Net:</span>
                        <span
                          className={`font-medium ${
                            metrics.profit >= 0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {metrics.profit >= 0 ? "+" : "-"}$
                          {Math.abs(metrics.profit).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Mobile toggle indicator - visual cue that card is tappable */}
                    <div className="md:hidden mt-3 w-full flex items-center justify-center gap-1 text-xs text-gray-600 py-2 border-t border-gray-100 pointer-events-none">
                      {showAllDetails ? (
                        <>
                          Show Less
                          <ChevronUp className="w-3 h-3" />
                        </>
                      ) : (
                        <>
                          Show More
                          <ChevronDown className="w-3 h-3" />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Highlight tag at bottom */}
                  {highlightTag ? (
                    <div
                      className={`w-full py-2 text-center text-xs font-bold ${highlightColor} border-t border-gray-200`}
                    >
                      {highlightTag}
                    </div>
                  ) : (
                    <div className="h-0">&nbsp;</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-4 mt-6 sm:mt-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Overall Performance
            </h2>
            <div className="flex flex-col xs:flex-row xs:items-center gap-1 xs:gap-2 sm:gap-3">
              <p className="text-gray-500 text-sm md:text-base">
                Based on $1 unit betting on every tip and every race (including
                exotics) <em>combined</em>, since inception.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-3 xs:gap-4">
        {metricConfigs.map((config) => {
          const value = systemMetrics[config.key];
          const formattedValue = config.formatter(value);
          const trend = config.trendCalculator(value);

          return (
            <MetricCard
              key={config.key}
              title={config.title}
              value={formattedValue}
              subtitle={config.description}
              icon={config.icon}
              trend={trend}
              className="hover:shadow-md transition-shadow"
            />
          );
        })}
      </div>

      {/* Context information */}
      <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="flex flex-col xs:flex-row xs:flex-wrap gap-2 xs:gap-4 sm:gap-6 text-xs xs:text-sm text-gray-500">
            <div className="flex-shrink-0">
              <span className="font-medium">Total Races:</span>{" "}
              {systemMetrics.totalRaces.toLocaleString()}
            </div>
            {systemMetrics.totalTips > 0 && (
              <div className="flex-shrink-0">
                <span className="font-medium">Tips Analysed:</span>{" "}
                {systemMetrics.totalTips.toLocaleString()}
              </div>
            )}
          </div>

          <div className="text-xxs xs:text-xs text-gray-400">
            <div className="truncate">
              Last updated:{" "}
              {systemMetrics.lastUpdated
                ? `${new Date(systemMetrics.lastUpdated).toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                    timeZone: DISPLAY_TIME_ZONE,
                  })}, ${formatTimeOfDay(systemMetrics.lastUpdated)}`
                : "Unknown"}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default AnalysisDashboard;
